import React, { useMemo, useState } from 'react';
import { Music, Video, BookOpen, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { useApp } from '../context/AppContext';

/**
 * MediaGallery - Shows all mantras across all topics that have any media (audio/video/meaningAudio).
 * onSelect(mantra, mediaType): copies mediaUrl + text + meaning from selected mantra to current.
 */
export default function MediaGallery({ open, onClose, mediaType, onSelect }) {
  const { topics } = useApp();
  const [search, setSearch] = useState('');

  const items = useMemo(() => {
    const result = [];
    topics.forEach(topic => {
      topic.mantras.forEach(m => {
        let mediaKey = '';
        if (mediaType === 'audioUrl') mediaKey = m.audioUrl;
        else if (mediaType === 'videoUrl') mediaKey = m.videoUrl;
        else if (mediaType === 'meaningAudioUrl') mediaKey = m.meaningAudioUrl;
        if (mediaKey) {
          result.push({
            mantraId: m.id,
            mantraName: m.name,
            text: m.text,
            meaning: m.meaning || '',
            mediaKey,
            audioUrl: m.audioUrl || '',
            videoUrl: m.videoUrl || '',
            meaningAudioUrl: m.meaningAudioUrl || '',
            topicTitle: topic.title,
            topicId: topic.id,
          });
        }
      });
    });
    return result;
  }, [topics, mediaType]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(it =>
      it.mantraName.toLowerCase().includes(q) ||
      it.text.toLowerCase().includes(q) ||
      it.topicTitle.toLowerCase().includes(q)
    );
  }, [items, search]);

  const typeLabel = mediaType === 'videoUrl' ? 'वीडियो' : mediaType === 'meaningAudioUrl' ? 'अर्थ ऑडियो' : 'ऑडियो';
  const TypeIcon = mediaType === 'videoUrl' ? Video : mediaType === 'meaningAudioUrl' ? BookOpen : Music;

  const handlePick = (item) => {
    onSelect({
      mediaKey: item.mediaKey,
      text: item.text,
      meaning: item.meaning,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="devanagari-ui flex items-center gap-2">
            <TypeIcon className="w-5 h-5 text-orange-600" />
            मीडिया गैलरी ({typeLabel})
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
          <p className="text-xs text-stone-600 devanagari-ui bg-orange-50 border border-orange-200 rounded p-2">
            💡 चुनने पर: मीडिया + मन्त्र पाठ + अर्थ कॉपी हो जाएगा
          </p>

          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="खोजें..."
              className="pl-8 devanagari-ui"
            />
          </div>

          {filtered.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-center py-8">
              <div>
                <TypeIcon className="w-10 h-10 mx-auto text-stone-300 mb-2" />
                <p className="text-sm text-stone-500 devanagari-ui">
                  {items.length === 0
                    ? `अभी तक कोई ${typeLabel} फ़ाइल अपलोड नहीं की गई।`
                    : 'कोई परिणाम नहीं मिला।'}
                </p>
              </div>
            </div>
          ) : (
            <ScrollArea className="flex-1 border border-orange-100 rounded-lg">
              <ul className="p-1">
                {filtered.map((item) => (
                  <li key={`${item.topicId}-${item.mantraId}`}>
                    <button
                      onClick={() => handlePick(item)}
                      className="w-full text-left p-2.5 rounded-md hover:bg-orange-50 transition-colors flex items-start gap-2"
                    >
                      <TypeIcon className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-800 devanagari-ui truncate">{item.mantraName}</p>
                        <p className="text-[11px] text-stone-500 devanagari truncate">{item.text}</p>
                        <p className="text-[10px] text-orange-700 devanagari-ui mt-0.5">{item.topicTitle}</p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="devanagari-ui">बंद करें</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
